
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from '@/components/ui/pagination';
import { Search, Flame, Zap, Recycle, AlertTriangle, Leaf, Package, Star, Plus, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { unifiedLibraryService } from '@/services/unifiedLibraryService';
import { UnifiedMaterial, UserMaterialData } from '@/types/unifiedLibrary';
import { useToast } from '@/hooks/use-toast';
import { EnhancedMaterialModal } from './EnhancedMaterialModal';

export const MaterialsLibrary = () => {
  const [materials, setMaterials] = useState<UnifiedMaterial[]>([]);
  const [userMaterials, setUserMaterials] = useState<UserMaterialData[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Materials');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMaterial, setSelectedMaterial] = useState<UnifiedMaterial | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const itemsPerPage = 50;
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [materialsData, userMaterialsData, categoriesData] = await Promise.all([
        unifiedLibraryService.getMaterials(),
        unifiedLibraryService.getUserMaterials(),
        unifiedLibraryService.getMaterialCategories()
      ]);

      setMaterials(materialsData);
      setUserMaterials(userMaterialsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading materials data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load materials library',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddMaterial = (material: UnifiedMaterial) => {
    setSelectedMaterial(material);
    setIsAddModalOpen(true);
  };

  const filteredMaterials = materials.filter(material => {
    const matchesSearch = material.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         material.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         material.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'All Materials' || material.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const totalPages = Math.ceil(filteredMaterials.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMaterials = filteredMaterials.slice(startIndex, startIndex + itemsPerPage);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Fossil Fuels': return <Flame className="h-4 w-4" />;
      case 'Biomass': return <Leaf className="h-4 w-4" />;
      case 'Metals': return <Zap className="h-4 w-4" />;
      case 'Waste': return <Recycle className="h-4 w-4" />;
      case 'Plastics': return <Package className="h-4 w-4" />;
      case 'Wood & Paper': return <Leaf className="h-4 w-4" />;
      case 'Textiles': return <Package className="h-4 w-4" />;
      case 'Electronics': return <Zap className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getScopeColor = (scope: string) => {
    switch (scope) {
      case 'Scope 1': return 'bg-red-100 text-red-800';
      case 'Scope 2': return 'bg-orange-100 text-orange-800';
      case 'Scope 3': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSustainabilityColor = (rating: number) => {
    if (rating >= 4) return 'bg-green-100 text-green-800';
    if (rating >= 3) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-3 w-3 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Materials Library</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading materials library...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Materials & Emission Factors Library</CardTitle>
          <div className="text-sm text-gray-600">
            Comprehensive database of {materials.length} materials with calorific values and emission factors
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search materials..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10"
              />
            </div>
            
            <Select value={selectedCategory} onValueChange={(value) => {
              setSelectedCategory(value);
              setCurrentPage(1);
            }}>
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All Materials">All Materials</SelectItem>
                {categories && categories.map(category => (
                  <SelectItem key={category} value={category}>{category}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Materials Table */}
          <div className="rounded-md border overflow-hidden mb-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Density</TableHead>
                  <TableHead>Carbon Footprint</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Sustainability</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedMaterials.map((material) => (
                  <TableRow key={material.id}>
                    <TableCell className="font-medium">{material.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(material.category)}
                        {material.category}
                      </div>
                    </TableCell>
                    <TableCell>{material.unit}</TableCell>
                    <TableCell>{material.density ? `${material.density} kg/m³` : 'N/A'}</TableCell>
                    <TableCell>{material.carbon_footprint ? `${material.carbon_footprint} kg CO2e` : 'N/A'}</TableCell>
                    <TableCell>
                      <Badge className={getScopeColor(material.scope)}>
                        {material.scope}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {renderStars(material.sustainability_rating || 0)}
                        <span className="text-xs text-gray-500">({material.sustainability_rating || 0}/5)</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            try {
                              console.log('Opening view modal for material:', material);
                              setSelectedMaterial(material);
                              setIsViewModalOpen(true);
                            } catch (error) {
                              console.error('Error opening view modal:', error);
                              toast({
                                title: 'Error',
                                description: 'Failed to open material details',
                                variant: 'destructive'
                              });
                            }
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAddMaterial(material)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between mt-6 pt-4 border-t border-gray-200 gap-4">
              <div className="text-sm text-gray-700 text-center sm:text-left">
                Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredMaterials.length)} of {filteredMaterials.length} results
              </div>
              <Pagination className="ml-auto">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                    return (
                      <PaginationItem key={pageNum}>
                        <PaginationLink
                          onClick={() => setCurrentPage(pageNum)}
                          isActive={currentPage === pageNum}
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Material Modal */}
      {selectedMaterial && (
        <EnhancedMaterialModal
          material={selectedMaterial}
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={() => {
            setIsAddModalOpen(false);
            loadData(); // Refresh user materials
            toast({
              title: 'Success',
              description: 'Material added to your organization',
            });
          }}
        />
      )}

      {/* Material View Modal */}
      {isViewModalOpen && (
        <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
          <DialogContent className="max-w-2xl">
            {!selectedMaterial ? (
              <div className="text-center py-8">
                <p className="text-gray-600">No material selected</p>
              </div>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    {selectedMaterial?.name || 'Material Details'}
                  </DialogTitle>
                </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Category</label>
                  <p className="text-sm text-gray-600">{selectedMaterial?.category || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Unit</label>
                  <p className="text-sm text-gray-600">{selectedMaterial?.unit || 'N/A'}</p>
                </div>
                {selectedMaterial.density && (
                  <div>
                    <label className="text-sm font-medium">Density</label>
                    <p className="text-sm text-gray-600">{selectedMaterial.density} kg/m³</p>
                  </div>
                )}
                {selectedMaterial.carbon_footprint && (
                  <div>
                    <label className="text-sm font-medium">Carbon Footprint</label>
                    <p className="text-sm text-gray-600">{selectedMaterial.carbon_footprint} kg CO2e</p>
                  </div>
                )}
                {selectedMaterial.cost_per_unit && (
                  <div>
                    <label className="text-sm font-medium">Cost per Unit</label>
                    <p className="text-sm text-gray-600">
                      {selectedMaterial.cost_per_unit} {selectedMaterial.currency}
                    </p>
                  </div>
                )}
                {selectedMaterial.sustainability_rating && (
                  <div>
                    <label className="text-sm font-medium">Sustainability Rating</label>
                    <div className="flex items-center gap-2">
                      <div className="flex">
                        {renderStars(selectedMaterial.sustainability_rating)}
                      </div>
                      <Badge className={getSustainabilityColor(selectedMaterial.sustainability_rating)}>
                        {selectedMaterial.sustainability_rating}/5
                      </Badge>
                    </div>
                  </div>
                )}
              </div>
              
              {selectedMaterial.description && (
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <p className="text-sm text-gray-600">{selectedMaterial.description}</p>
                </div>
              )}
              
              {selectedMaterial.tags && selectedMaterial.tags.length > 0 && (
                <div>
                  <label className="text-sm font-medium">Tags</label>
                  <div className="flex flex-wrap gap-2">
                    {selectedMaterial.tags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedMaterial.scope && (
                <div>
                  <label className="text-sm font-medium">Scope</label>
                  <Badge className={getScopeColor(selectedMaterial.scope)}>
                    {selectedMaterial.scope}
                  </Badge>
                </div>
              )}
              
              {selectedMaterial.metadata && Object.keys(selectedMaterial.metadata).length > 0 && (
                <div>
                  <label className="text-sm font-medium">Additional Information</label>
                  <div className="mt-2 space-y-2">
                    {Object.entries(selectedMaterial.metadata).map(([key, value]) => (
                      <div key={key} className="p-2 bg-gray-50 rounded border">
                        <div className="flex justify-between items-start text-sm">
                          <span className="font-medium text-gray-900">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                          </span>
                          <span className="text-gray-700 ml-2 text-right">
                            {typeof value === 'string' ? (
                              value
                            ) : typeof value === 'number' ? (
                              value.toString()
                            ) : typeof value === 'boolean' ? (
                              value ? 'Yes' : 'No'
                            ) : Array.isArray(value) ? (
                              <div className="flex flex-wrap gap-1">
                                {value.map((item, index) => (
                                  <Badge key={index} variant="outline" className="text-xs">
                                    {String(item)}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              String(value)
                            )}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
